import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import EmailPrompt from './EmailPrompt';
import { useMyHomeTiles, useConfirmProxy, ProxyAuthorizationRequestStatus } from '@sentara-health/sentara-api-hooks-core';
import { GlobalContext, isAvMed } from 'context';
import { useNavigate } from 'react-router-dom';
import {
  ProfileUpdate,
  SHOW_PREFERENCE_POPUP,
  getLocalStorageBoolean,
  setSessionStorageBoolean,
  getSessionStorageBoolean,
  GenerateTiles,
  isGuest,
  NavigateConst,
  DeviceTypes,
  useDeviceType,
  WELLNESS_PLAN_CONST,
   useProxyStatusEffect,
  useProxyStatus,
  ProxyPopupState,
  createInitialProxyPopupState,
  mapAlertDetailsToPopupState,
  useProxyRead,
  hasActiveDesignation,
  ANALYTICS_MESSAGES,
  useAnalytics,
} from 'common';
import { DASHBOARD_TILES } from 'common/utils/constants/Constants';
import ApiWrapper from '../../common/ApiWrapper';
import HomeTiles from './HomeTiles';
import {
  ModalOverlay,
  Button,
  Headings,
  Loader,
  Paragraph,
  ModalMultiUse,
  TooltipBanner,
  DetailedAlertMessages,
} from '@sentara-health/optimahealth_web';
import { CareTile, PaperlessPreferencePopup } from 'templates';
import { usePreferenceSearchAPI } from 'common/utils/PreferenceSearchAPIUtil';
import { set } from 'date-fns';

interface Tile {
  name: string;
  icon: React.JSX.Element;
  id: string;
}

const Dashboard = () => {
 

  const { getFeature, loginMemberId, planWellness, profileState, proxyAuthorizationLinkData, setApprovedDesignators } =
    useContext(GlobalContext);
  const { MyHomeTilesType } = useMyHomeTiles();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === DeviceTypes.DESKTOP;
  const navigate = useNavigate();
  const { getProxyRead } = useProxyRead();
  const { checkProxyAccessStatus } = useProxyStatus();
  const [allTiles, setAllTiles] = useState<Tile[]>([]);
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showToolTipBanner, setShowToolTipBanner] = useState<boolean>(false);
  const [acceptedProxyName, setAcceptedProxyName] = useState<string>('');
  const profileUpdate = getLocalStorageBoolean(ProfileUpdate);
  const { data: dashboardData, callApi: callUpdateDashboard }: any =
    ApiWrapper(MyHomeTilesType);
  const effectRan = useRef(false);
  const dashboardEffectRan = useRef(false);

  const { trackContentListing } = useAnalytics();

  /***Generate Tiles and display tiles in dashboard based on launchDarkly*/
  useEffect(() => {
    (async () => {
      const guestValue: boolean = await isGuest();
      if (dashboardEffectRan.current) return;
      if (getFeature?.DesignatedRepresentativeTab && guestValue) {
        const readResponse = await getProxyRead();
        const hasDesignation = hasActiveDesignation(readResponse);

        dashboardEffectRan.current = true;

        if (isAvMed()) {
          if (hasDesignation) {
            navigate(NavigateConst.guestSharedAccounts);
          } else {
            const verified = await checkProxyAccessStatus();
            navigate(
              verified
                ? NavigateConst.guestSharedAccounts
                : NavigateConst.guestDefault
            );
          }
        } else {
          if (hasDesignation) {
            navigate(NavigateConst.guestSharedAccounts);
          } else {
            const verified = await checkProxyAccessStatus();
            navigate(
              verified
                ? NavigateConst.guestSharedAccounts
                : NavigateConst.enrollBenefits
            );
          }
        }
        return;
      }
      if (getFeature && !guestValue) {
        dashboardEffectRan.current = true;
        callUpdateDashboard();
      }
    })();
  }, [callUpdateDashboard, getFeature, navigate, getProxyRead, checkProxyAccessStatus]);

  /***Generate Tiles and display tiles in dashboard based on launchDarkly*/
  useEffect(() => {
    let tilesToRender = dashboardData;
    if (isAvMed()) {
      tilesToRender = DASHBOARD_TILES;
    }
    const getItems = GenerateTiles(tilesToRender, getFeature, t);
    setAllTiles(getItems);
  }, [dashboardData, getFeature, t]);

  function popupClose() {
    localStorage.setItem(ProfileUpdate, 'false');
    setIsOpen(false);
  }
  useEffect(() => {
    if (profileUpdate) {
      setIsOpen(false);
    }
  }, [profileUpdate]);

  const clickCare = () => {
    trackContentListing('902', ANALYTICS_MESSAGES.FIND_DOCTOR_BANNER_CLICK, 1);
    navigate(NavigateConst.findADoctor);
  };

  const handleBannerLoadError = useCallback(() => {
    trackContentListing(
      '903',
      ANALYTICS_MESSAGES.DASHBOARD_BANNER_LOAD_ERROR,
      2
    );
  }, [trackContentListing]);

  const heroImgRef = useRef<HTMLDivElement>(null);

  // CSS background images never fire error events, so load the URL in an Image to detect failures.
  useEffect(() => {
    const heroEl = heroImgRef.current;
    if (!heroEl) return;
    let img: HTMLImageElement | null = null;
    let lastUrl: string | undefined;

    const checkBanner = () => {
      // Extracts the first URL from e.g. url("a.svg"); undefined for 'none' or gradients.
      const url = /url\(["']?(.*?)["']?\)/.exec(
        getComputedStyle(heroEl).backgroundImage
      )?.[1];
      if (!url || url === lastUrl) return;
      lastUrl = url;
      if (img) img.onerror = null;
      img = new Image();
      img.onerror = handleBannerLoadError;
      img.src = url;
    };

    checkBanner();
    // Catches banner changes made after mount, by React (async wellness class) or the Adobe Launch script.
    const observer = new MutationObserver(checkBanner);
    observer.observe(heroEl, { attributeFilter: ['style', 'class'] });

    return () => {
      observer.disconnect();
      if (img) img.onerror = null;
    };
  }, []);

  const [smsConsent, setSmsConsent] = useState<boolean>(false);
  const [emailConsent, setEmailConsent] = useState<boolean>(false);

   const [proxyPopupState, setProxyPopupState] = useState<ProxyPopupState>(
    createInitialProxyPopupState()
  );
  const { confirmProxyAuthorization } = useConfirmProxy();
  const hasUserDismissedPopup = useRef(false);
  const isProxyModalTransitioning = useRef(false);
  const denyTransitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const pendingAlertDetailsRef = useRef<ProxyPopupState['alertDetails']>(null);

  // Check proxy status on mount and handle status changes
  useProxyStatusEffect({
    onStatusChange: (details) => {
      // Only show popup if user hasn't dismissed it
      if (!hasUserDismissedPopup.current && !isProxyModalTransitioning.current) {
        const newPopupState = mapAlertDetailsToPopupState(details);
        setProxyPopupState(newPopupState);
      }
    },
  });

  useEffect(() => {
    return () => {
      if (denyTransitionTimeoutRef.current) {
        clearTimeout(denyTransitionTimeoutRef.current);
      }
    };
  }, []);

  const { fetchProfile, fetchMemberDetailsAPI } = usePreferenceSearchAPI();

  useEffect(() => {
    (async () => {
      if (effectRan.current) return;
      effectRan.current = true; // Set immediately

      const guestValue: boolean = await isGuest();

      if (!guestValue && loginMemberId && loginMemberId.trim() !== '') {
        fetchMemberDetailsAPI();
        fetchProfile();
      } else {
        effectRan.current = false; // Reset if not calling fetchProfile
      }
    })();
  }, [loginMemberId]);

  useEffect(() => {
    if (
      profileState &&
      (profileState?.smsConsent || profileState?.paperlessConsent)
    ) {
      setSmsConsent(!profileState?.smsConsent?.optInConsent);
      setEmailConsent(!profileState?.paperlessConsent?.optInConsent);
    }
  }, [profileState]);

  function preferencesPopupClose() {
    setSmsConsent(false);
    setEmailConsent(false);
    setSessionStorageBoolean(SHOW_PREFERENCE_POPUP, true);
  }

   const closeProxyPopup = () => {
    if (isProxyModalTransitioning.current) {
      return;
    }
    if (denyTransitionTimeoutRef.current) {
      clearTimeout(denyTransitionTimeoutRef.current);
      denyTransitionTimeoutRef.current = null;
    }
    isProxyModalTransitioning.current = false;
    pendingAlertDetailsRef.current = null;
    hasUserDismissedPopup.current = true;
    setProxyPopupState(createInitialProxyPopupState());
  };

  // X/cross dismissal of the accept/deny popup records the request as still Pending.
  const handleProxyModalDismiss = async () => {
    const currentAlertDetails = proxyPopupState.alertDetails;
    if (proxyPopupState.showApprovalPopup && currentAlertDetails) {
      trackContentListing(
        ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_CANCEL_CLICK.contentId,
        ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_CANCEL_CLICK.contentTitle,
        ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_CANCEL_CLICK.itemPosition
      );
      try {
        await confirmProxyAuthorization({
          proxies: [
            {
              personaType: 'Caregiver',
              identifier: [
                {
                  system: 'designatorLoginId',
                  value: proxyAuthorizationLinkData?.forgerockUuid ?? '',
                },
                {
                  system: 'correlationId',
                  value: proxyAuthorizationLinkData?.correlationId ?? '',
                },
              ],
              Consents: [
                {
                  ProxyAuthorizationRequestStatus:
                    ProxyAuthorizationRequestStatus.Pending,
                },
              ],
            },
          ],
          audit: {
            InitiatedDate: new Date(),
            ModifiedDate: new Date(),
          },
        });
      } catch (error) {
        console.error('Error confirming proxy authorization (pending):', error);
      }
    }
    closeProxyPopup();
  };

  const showDenyRequestPopup = () => {
    const currentAlertDetails = proxyPopupState.alertDetails;
    if (!currentAlertDetails) return;

    trackContentListing(
      ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_DENY_CLICK.contentId,
      ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_DENY_CLICK.contentTitle,
      ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_DENY_CLICK.itemPosition
    );

    if (denyTransitionTimeoutRef.current) {
      clearTimeout(denyTransitionTimeoutRef.current);
      denyTransitionTimeoutRef.current = null;
    }

    // Close accept/deny modal first, then open deny request modal.
    hasUserDismissedPopup.current = true;
    isProxyModalTransitioning.current = true;
    pendingAlertDetailsRef.current = currentAlertDetails;
    setProxyPopupState(createInitialProxyPopupState());

    denyTransitionTimeoutRef.current = setTimeout(() => {
      setProxyPopupState({
        ...createInitialProxyPopupState(),
        showDeniedPopup: true,
        alertDetails: pendingAlertDetailsRef.current,
      });
      isProxyModalTransitioning.current = false;
      denyTransitionTimeoutRef.current = null;
    }, 220);
  };

  const confirmDenyRequest = async () => {
    const currentAlertDetails = proxyPopupState.alertDetails;
    if (!currentAlertDetails) {
      closeProxyPopup();
      return;
    }

    try {
      await confirmProxyAuthorization({
        proxies: [
          {
            personaType: 'Caregiver',
            identifier: [
              {
                system: 'designatorLoginId',
                value: proxyAuthorizationLinkData?.forgerockUuid ?? '',
              },
              {
                system: 'correlationId',
                value: proxyAuthorizationLinkData?.correlationId ?? '',
              },
            ],
            Consents: [
              {
                ProxyAuthorizationRequestStatus:
                  ProxyAuthorizationRequestStatus.Denied,
              },
            ],
          },
        ],
        audit: {
          InitiatedDate: new Date(),
          ModifiedDate: new Date(),
        },
      });
    } catch (error) {
      console.error('Error confirming proxy authorization (deny):', error);
    }

    setProxyPopupState({
      ...createInitialProxyPopupState(),
      showDeniedConfirmation: true,
      alertDetails: currentAlertDetails,
    });
  };

  const proxyModalContent = (() => {
    if (proxyPopupState.showApprovalPopup && proxyPopupState.alertDetails) {
      return {
        header: t('profile.designatedRepresentative.designatedRepRequestTitle'),
        body: (
          <Paragraph
            text={t('profile.designatedRepresentative.designatedRepRequestDesc', {
              fieldName: proxyPopupState.alertDetails.name,
            })}
          />
        ),
        footer: (
          <>
            <div className="col-md-6 col-12 mb-md-0 mb-3 ps-sm-0 pe-sm-2 px-0">
              <Button
                dataTestId="acceptProxyRequestButton"
                variant="primary_btn"
                className="btn-md-height w-100"
                onClick={async () => {
                  const currentAlertDetails = proxyPopupState.alertDetails;
                  if (currentAlertDetails) {
                    trackContentListing(
                      ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_ACCEPT_CLICK.contentId,
                      ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_ACCEPT_CLICK.contentTitle,
                      ANALYTICS_MESSAGES.DR_CONTENT_LISTING.DESIGNATED_REP_ACCESS_ACCEPT_CLICK.itemPosition
                    );
                    setAcceptedProxyName(currentAlertDetails.name );
                    try {
                      await confirmProxyAuthorization({
                        proxies: [
                          {
                            personaType: 'Caregiver',
                            identifier: [
                              {
                                system: 'designatorLoginId',
                                value: proxyAuthorizationLinkData?.forgerockUuid ?? '',
                              },
                              {
                                system: 'correlationId',
                                value: proxyAuthorizationLinkData?.correlationId ?? '',
                              },
                            ],
                            Consents: [
                              {
                                ProxyAuthorizationRequestStatus:
                                  ProxyAuthorizationRequestStatus.Approved,
                              },
                            ],
                          },
                        ],
                        audit: {
                          InitiatedDate: new Date(),
                          ModifiedDate: new Date(),
                        },
                      });
                      // Reflect the newly approved account in the profile dropdown without a reload.
                      setApprovedDesignators((prev: Array<{ name: string; status: string }>) =>
                        prev.some((designator) => designator.name === currentAlertDetails.name)
                          ? prev
                          : [
                              ...prev,
                              {
                                name: currentAlertDetails.name,
                                status: ProxyAuthorizationRequestStatus.Approved,
                              },
                            ]
                      );
                    } catch (error) {
                      console.error('Error confirming proxy authorization (accept):', error);
                    }
                  }
                  closeProxyPopup();
                  setShowToolTipBanner(true); // Show the tooltip banner after accepting the request
                }}
              >
                {t('common.accept')}
              </Button>
            </div>
            <div className="col-md-6 col-12 ps-sm-2 pe-sm-0 px-0">
              <Button
                dataTestId="denyProxyRequestButton"
                variant="secondary_btn"
                className="btn-md-height w-100"
                onClick={showDenyRequestPopup}
              >
                {t('common.deny')}
              </Button>
            </div>
          </>
        ),
      };
    }

    if (proxyPopupState.showDeniedPopup && proxyPopupState.alertDetails) {
      return {
        header: t('profile.designatedRepresentative.designatedRepDenyRequestTitle'),
        body: (
          <Paragraph
            text={t('profile.designatedRepresentative.designatedRepDenyRequestDesc', {
              fieldName: proxyPopupState.alertDetails.name,
            })}
          />
        ),
        footer: (
          <>
            <div className="col-md-6 col-12 mb-md-0 mb-3 ps-sm-0 pe-sm-2 px-0">
              <Button
                dataTestId="confirmDenyProxyRequestButton"
                variant="primary_btn"
                className="btn-md-height w-100"
                onClick={confirmDenyRequest}
              >
                {t('profile.designatedRepresentative.yesDenyRequest')}
              </Button>
            </div>
            <div className="col-md-6 col-12 ps-sm-2 pe-sm-0 px-0">
              <Button
                dataTestId="cancelDenyProxyRequestButton"
                variant="secondary_btn"
                className="btn-md-height w-100"
                onClick={closeProxyPopup}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </>
        ),
      };
    }

    // Show "Request Denied" confirmation after user confirms denial
    if (proxyPopupState.showDeniedConfirmation && proxyPopupState.alertDetails) {
      return {
        header: t('profile.designatedRepresentative.deniedTitle'),
        body: (
          <Paragraph
            text={t('profile.designatedRepresentative.deniedDescription', {
              Designator: proxyPopupState.alertDetails.name,
            })}
          />
        ),
        footer: (
          <div className="col-12 px-0">
            <Button
              dataTestId="closeDeniedProxyRequestButton"
              variant="secondary_btn"
              className="btn-md-height w-100"
              onClick={() => {
                closeProxyPopup();
              }}
            >
              {t('common.close')}
            </Button>
          </div>
        ),
      };
    }

    // Show "DR Invitation Denied" popup when member denies DR's request
    if (proxyPopupState.showInvitationPopup && proxyPopupState.alertDetails) {
      return {
        header: t('profile.designatedRepresentative.designatedRepResendInviteTitle'),
        body: (
          <Paragraph
            text={t('profile.designatedRepresentative.designatedRepResendInviteDesc', {
              fieldName: proxyPopupState.alertDetails.name,
            })}
          />
        ),
        footer: (
          <>
            <div className="col-md-6 col-12 mb-md-0 mb-3 ps-sm-0 pe-sm-2 px-0">
              <Button
                dataTestId="sendNewInvitationButton"
                variant="primary_btn"
                className="btn-md-height w-100"
                onClick={() => {
                  closeProxyPopup();
                  navigate(NavigateConst.profile);
                }}
              >
                {t('profile.designatedRepresentative.designatedRepResendInviteBtn')}
              </Button>
            </div>
            <div className="col-md-6 col-12 ps-sm-2 pe-sm-0 px-0">
              <Button
                dataTestId="closeInvitationPopupButton"
                variant="secondary_btn"
                className="btn-md-height w-100"
                onClick={closeProxyPopup}
              >
                {t('common.close')}
              </Button>
            </div>
          </>
        ),
      };
    }

    return null;
  })();


  const textNotificationsFeature = getFeature?.TextNotifications ?? false;
  const emailConsentFeature = getFeature?.EmailConsent ?? false;
  const communicationPreferencesPopup =
    getFeature?.TextNotificationPopup || getFeature?.EmailConsentPopUp;
  const showBannerImg = isAvMed()
    ? 'hero-img-avmed'
    : getFeature?.WellnessPrograms && planWellness === WELLNESS_PLAN_CONST
    ? 'hero-img-guest-shp'
    : 'hero-img-shp';

  const currentLanguage = i18n.language;
  const languageClass = currentLanguage === 'es' ? 'lang-es' : '';

  return (
    <div>
      {loading && <Loader />}
            {/* Expired Access Banner */}
      {proxyPopupState.showExpiredBanner && (
        <div>
          <DetailedAlertMessages
            message={t('dashboard.proxyAccessRemovedTitle', {
              name: proxyPopupState.alertDetails?.name || 'the member',
            })}
            additionalInfo={t('proxyApproval.proxyAccessExpiredMessage')}
            variant="dangerLite"
            type="danger"
            collapsible={false}
            dismissible={true}
            onDismiss={() =>
              setProxyPopupState({
                ...proxyPopupState,
                showExpiredBanner: false,
              })
            }
            data-testid="proxyExpiredBanner"
          />
        </div>
      )}

      {/* Terminated Access Banner */}
      {proxyPopupState.showTerminatedBanner && (
        <div>
          <DetailedAlertMessages
            message={t('dashboard.proxyAccessRemovedByRepTitle', {
              name: proxyPopupState.alertDetails?.name || 'the member',
            })}
            additionalInfo={t('dashboard.proxyAccessRemovedByRepMessage')}
            variant="dangerLite"
            type="danger"
            collapsible={false}
            dismissible={true}
            onDismiss={() =>
              setProxyPopupState({
                ...proxyPopupState,
                showTerminatedBanner: false,
              })
            }
            data-testid="proxyTerminatedBanner"
          />
        </div>
      )}


      <div className={`dashboard mb-4 ${isAvMed() ? 'dashboard-bg-img' : ''}`}>
        <div className="hero-section d-flex">
          {loginMemberId && loginMemberId.trim() !== '' ? (
            <div
              className="profile-id-text pt-2 col-12 visually-hidden"
              data-testid="profileIdBenefits"
            >
              {loginMemberId}
            </div>
          ) : null}

          <div
            ref={heroImgRef}
            data-testid={'heroImgId'}
            className={`hero-img ${showBannerImg} ${languageClass}`}
          >
            <div className="hero-container d-flex flex-column p-sm-0 p-4">
              {getFeature?.FindADoctor && (
                <div className="inner-cont d-flex flex-column">
                  <Headings
                    level={1}
                    text={t('dashboard.careProviderTitle')}
                    variant="semiBold"
                    className="mt-n3"
                  />
                  <Paragraph
                    text={t('dashboard.shortBlurb')}
                    variant="regular"
                    className="mt-n3 mb-1 w-75"
                  />
                  <Button
                    dataTestId={'findCareButton'}
                    variant="primary_btn"
                    onClick={() => clickCare()}
                  >
                    {t('dashboard.findCare')}
                  </Button>
                </div>
              )}

              {getFeature?.WellnessPrograms &&
                planWellness === WELLNESS_PLAN_CONST && (
                  <div className="inner-cont d-flex flex-column w-75">
                    <Headings
                      level={1}
                      text={t('dashboard.welcomeHeading')}
                      data-testid="guestWelcomeText"
                      variant="semiBold"
                    />
                    <Paragraph
                      text={t('dashboard.welcomeInfo')}
                      variant="regular"
                      className="mt-n3 mb-1 w-75"
                    />
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
      {getFeature?.HomeTiles && (
        <div className="grid-cont d-flex">
          <div
            className="left-border-white"
            aria-hidden="true"
            data-focusable="false"
          ></div>
          <HomeTiles allTiles={allTiles} />
          <div
            className="bottom-border-white"
            aria-hidden="true"
            data-focusable="false"
          ></div>
        </div>
      )}
      {getFeature?.WellnessPrograms && planWellness === WELLNESS_PLAN_CONST && (
        <div className="plan-grid-cont d-flex">
            <div
              className="left-border-white"
              aria-hidden="true"
              data-focusable="false"
            ></div>
            <CareTile
              dataTestId="guestWebMD"
              className="align-self-start caretile-border-bottom"
              fontAwesomeIconKey="clipboard-list-check"
              heading={t('dashboard.benefitsTitle')}
              subHeading={t('dashboard.benefitsContent') ?? ''}
              onClick={() => navigate(NavigateConst.enrollBenefits)}
            />
            <CareTile
              dataTestId="guestWebMD"
              className="align-self-start caretile-border-bottom"
              fontAwesomeIconKey="hand-holding-heart"
              heading={t('dashboard.wellnessTitle')}
              subHeading={t('dashboard.wellnessContent') ?? ''}
              onClick={() => navigate(NavigateConst.wellnessProgram)}
            />
            <div
            className="bottom-border-white"
            aria-hidden="true"
            data-focusable="false"
          ></div>
        </div>
      )}

      {getFeature?.UpdateEmailPrompt && isOpen && (
        <ModalOverlay
          isOpen
          onClose={popupClose}
          overlayInfo="Modal information"
        >
          <EmailPrompt setIsOpen={setIsOpen} />
        </ModalOverlay>
      )}
      {!getSessionStorageBoolean(SHOW_PREFERENCE_POPUP) &&
        profileState &&
        (profileState as { isMemberEnrolled: boolean })?.isMemberEnrolled &&
        ((smsConsent &&
          textNotificationsFeature &&
          communicationPreferencesPopup) ||
          (emailConsent &&
            emailConsentFeature &&
            communicationPreferencesPopup)) && (
          <PaperlessPreferencePopup
            smsConsent={smsConsent}
            emailConsent={emailConsent}
            closeModal={preferencesPopupClose}
          />
        )}
            {/* All Proxy Modals */}
      {getFeature?.DesignatedRepresentativeTab && proxyModalContent && (
        <ModalMultiUse
          isOpen={true}
          onClose={handleProxyModalDismiss}
          header={proxyModalContent.header}
          btnClose={true}
          variant="defaultNoPyBody"
          body={proxyModalContent.body}
          footer={proxyModalContent.footer}
          data-testid="proxyModal"
        />
      )}
      {/* DR success banner — centered under the profile button on desktop, screen-center on mobile */}
      {showToolTipBanner && (
          <div
            className={`position-absolute overflow-visible dr-banner-pos designated-rep-tooltip-banner 
            ${isDesktop ? 'end-0 dr-banner-pos-desktop' : 'start-50 translate-middle-x'
              }`}
          >
            <TooltipBanner
              open={showToolTipBanner}
              onClose={() => setShowToolTipBanner(false)}
              onButtonClick={() => navigate(NavigateConst.profile)}
              buttonLabel={t('dashboard.proxyAlertBannerActionButton')}
              title={t('dashboard.proxyAlertBannerTitle', { memberName: acceptedProxyName || 'the member' })}
              description={t('dashboard.proxyAlertBannerDescription')}
              placement="bottom"
              arrowAlignment="right"
              autoHideDuration={3000}
              dataTestId="designatedRepToolTipBanner"
              width={'325px'}
            />
          </div>
      )}
    </div>
  );
};
Dashboard.propTypes = {};

export default Dashboard;
